import { useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../store/authStore';

interface LoginForm {
  email: string;
  password: string;
}

/** Water-marked tree in the brand panel — the only place the mark is used. */
function TreeWatermark() {
  return (
    <svg
      width="620"
      height="620"
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden
      style={{ position: 'absolute', bottom: -150, right: -170, opacity: 0.07 }}
    >
      <path d="M16 30V19.5" stroke="#8B3A0F" strokeWidth="1.4" strokeLinecap="round" />
      <path
        d="M16 23.5L12 20M16 21L19.5 17.5"
        stroke="#8B3A0F"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
      <path
        d="M16 3.2c2.4 0 4 1.4 4.5 3 2.4-.4 4.4 1.1 4.6 3.2 2 .5 3.1 2.3 2.6 4.1 1.6 1.1 1.9 3.2.7 4.6-1 1.2-2.7 1.6-4.1 1-.6 1.7-2.4 2.7-4.2 2.3-.7 1.6-2.4 2.5-4.1 2.2-1.7.3-3.4-.6-4.1-2.2-1.8.4-3.6-.6-4.2-2.3-1.4.6-3.1.2-4.1-1-1.2-1.4-.9-3.5.7-4.6-.5-1.8.6-3.6 2.6-4.1.2-2.1 2.2-3.6 4.6-3.2.5-1.6 2.1-3 4.5-3z"
        stroke="#8B3A0F"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const LoginPage = () => {
  const { login, isLoggingIn, loginError } = useAuth();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onFinish = (values: LoginForm) => {
    login(values);
  };

  let errorMessage: string | null = null;
  if (loginError) {
    const axiosErr = loginError as AxiosError<{ detail?: string }>;
    errorMessage = axiosErr.response?.data?.detail ?? 'Помилка входу. Перевірте дані.';
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'auto',
        display: 'grid',
        gridTemplateColumns: '1.15fr 1fr',
        minWidth: 980,
        background: 'var(--bg)',
        color: 'var(--text)',
      }}
    >
      {/* ── Brand panel ── */}
      <div
        style={{
          background: 'var(--bg-sidebar)',
          borderRight: '1px solid var(--border-sidebar)',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '0 72px',
        }}
      >
        <TreeWatermark />
        <div style={{ position: 'relative' }}>
          <div
            className="wordmark"
            style={{ fontSize: 52, lineHeight: 1.05, color: 'var(--text)', whiteSpace: 'nowrap' }}
          >
            Dniprowood
          </div>
          <div
            style={{
              fontFamily: 'var(--font-script)',
              fontWeight: 700,
              fontSize: 40,
              color: 'var(--brand)',
              letterSpacing: '0.01em',
              lineHeight: 1,
              marginTop: 1,
              whiteSpace: 'nowrap',
            }}
          >
            enterprise resource planning
          </div>
          <div
            style={{
              width: 52,
              height: 2,
              background: 'var(--border-strong)',
              margin: '30px 0 22px',
            }}
          />
          <div
            style={{
              fontSize: 15,
              lineHeight: 1.6,
              color: 'var(--text-secondary)',
              maxWidth: 420,
            }}
          >
            Облік дубової деревини від закупівлі сировини до відвантаження готової продукції —
            сушіння, переробка, склади, замовлення.
          </div>
        </div>
      </div>

      {/* ── Form panel ── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '0 72px',
        }}
      >
        <div style={{ maxWidth: 352, width: '100%' }}>
          <div style={{ fontSize: 23, fontWeight: 700, letterSpacing: '-0.02em' }}>
            Вхід до системи
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 5 }}>
            Введіть робочу пошту та пароль
          </div>

          {errorMessage && (
            <div
              style={{
                display: 'flex',
                gap: 9,
                alignItems: 'flex-start',
                background: 'var(--error-bg)',
                border: '1px solid var(--error-border)',
                borderRadius: 10,
                padding: '11px 13px',
                marginTop: 20,
              }}
            >
              <span
                style={{ color: 'var(--error)', fontSize: 13, fontWeight: 700, lineHeight: 1.4 }}
              >
                !
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--error)', lineHeight: 1.45 }}>
                {errorMessage}
              </span>
            </div>
          )}

          <Form<LoginForm>
            name="login"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
            style={{ marginTop: 24 }}
          >
            <Form.Item
              label="Робоча пошта"
              name="email"
              rules={[
                { required: true, message: 'Введіть email' },
                { type: 'email', message: 'Некоректний email' },
              ]}
            >
              <Input placeholder="ім'я@dniprowood.com" autoComplete="email" size="large" />
            </Form.Item>

            <Form.Item
              label="Пароль"
              name="password"
              rules={[{ required: true, message: 'Введіть пароль' }]}
            >
              <Input.Password placeholder="••••••••" autoComplete="current-password" size="large" />
            </Form.Item>

            <Form.Item style={{ marginTop: 20, marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoggingIn}
                block
                style={{ height: 46, borderRadius: 10, fontSize: 14 }}
              >
                Увійти
              </Button>
            </Form.Item>
          </Form>

          <div
            style={{
              fontSize: 12.5,
              color: 'var(--text-secondary)',
              marginTop: 22,
              lineHeight: 1.5,
            }}
          >
            Доступ до системи надає адміністратор. Якщо не можете увійти — зверніться до нього.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
